import * as React from 'react'
import { Accordion } from '#app/components/landings/blocks/Accordion.tsx'
import { AudiencePaths } from '#app/components/landings/blocks/AudiencePaths.tsx'
import { BlogBlock } from '#app/components/landings/blocks/BlogBlock.tsx'
import { CollectionsCarousel } from '#app/components/landings/blocks/CollectionCarousel.tsx'
import { ComparisonList } from '#app/components/landings/blocks/ComparisonList.tsx'
import { CoursesGrid } from '#app/components/landings/blocks/CoursesGrid.tsx'
import { FeaturesGrid } from '#app/components/landings/blocks/FeaturesGrid.tsx'
import { FeatureSplit } from '#app/components/landings/blocks/FeatureSplit.tsx'
import { FooterBlock } from '#app/components/landings/blocks/FooterBlock.tsx'
import { GlossaryCompare } from '#app/components/landings/blocks/GlossaryCompare.tsx'
import { GridList } from '#app/components/landings/blocks/GridList.tsx'
import { HeroModern } from '#app/components/landings/blocks/HeroModern.tsx'
import { LearningPhaseCard } from '#app/components/landings/blocks/LearningPhaseCard.tsx'
import { LearningPhaseShowcase } from '#app/components/landings/blocks/LearningPhaseShowcase.tsx'
import { LearningSystemExplainer } from '#app/components/landings/blocks/LearningSystemExplainer.tsx'
import { PricingBlock } from '#app/components/landings/blocks/PricingBlock.tsx'
import { PricingSnapshot } from '#app/components/landings/blocks/PricingSnapshot.tsx'
import { ProductWalkthroughAccordion } from '#app/components/landings/blocks/ProductWalkthroughAccordion.tsx'
import { ProfileEntryPoints } from '#app/components/landings/blocks/ProfileEntryPoints.tsx'
import { ScreenshotShowcase } from '#app/components/landings/blocks/ScreenshotShowcase.tsx'
import { SimpleCta } from '#app/components/landings/blocks/SimpleCta.tsx'
import { Statement } from '#app/components/landings/blocks/Statement.tsx'
import { TestimonialsBlock } from '#app/components/landings/blocks/TestimonialsBlock.tsx'
import {
	type LandingBlockType,
	type LandingPageData,
} from '#app/config/schemas/landingPageSchema.ts'
import { clientLogger } from '#app/utils/pz/logger/clientLogger'

const log = clientLogger.scoped('LandingPageRenderer')

function LegacyHeroBlock(props: {
	title?: string
	subtitle?: string
	ctaText?: string
	ctaLink?: string
	alignment?: 'left' | 'center' | 'right'
}) {
	return (
		<HeroModern
			title={props.title ?? ''}
			subtitle={props.subtitle}
			align={props.alignment === 'left' ? 'left' : 'center'}
			buttons={
				props.ctaText && props.ctaLink
					? [{ text: props.ctaText, href: props.ctaLink }]
					: undefined
			}
		/>
	)
}

function LegacyFeaturesBlock(props: {
	title?: string
	subtitle?: string
	columns?: number
	items?: Array<{
		title?: string
		description?: string
		icon?: string
	}>
}) {
	return (
		<FeaturesGrid
			title={props.title}
			subtitle={props.subtitle}
			columns={props.columns}
			items={
				props.items?.map((item) => ({
					title: item.title ?? '',
					description: item.description ?? '',
					icon: item.icon,
				})) ?? []
			}
		/>
	)
}

function LegacyCtaBlock(props: {
	headline?: string
	subheadline?: string
	buttonText?: string
	buttonLink?: string
}) {
	return (
		<SimpleCta
			headline={props.headline ?? ''}
			subheadline={props.subheadline}
			buttonText={props.buttonText ?? ''}
			buttonLink={props.buttonLink ?? ''}
		/>
	)
}

function LegacyFaqBlock(props: {
	title?: string
	description?: string
	items?: Array<{
		question: string
		answer: string
	}>
}) {
	return (
		<Accordion
			title={props.title}
			description={props.description}
			items={props.items ?? []}
		/>
	)
}

const BLOCK_COMPONENTS = {
	Hero: LegacyHeroBlock,
	Features: LegacyFeaturesBlock,
	CTA: LegacyCtaBlock,
	Footer: FooterBlock,
	Testimonials: TestimonialsBlock,
	Pricing: PricingBlock,
	Blog: BlogBlock,
	FAQ: LegacyFaqBlock,
	HeroModern: HeroModern,
	LearningPhaseCard: LearningPhaseCard,
	LearningPhaseShowcase: LearningPhaseShowcase,
	LearningSystemExplainer: LearningSystemExplainer,
	GridList: GridList,
	AudiencePaths: AudiencePaths,
	FeaturesGrid: FeaturesGrid,
	GlossaryCompare: GlossaryCompare,
	Statement: Statement,
	FeatureSplit: FeatureSplit,
	ScreenshotShowcase: ScreenshotShowcase,
	ComparisonList: ComparisonList,
	Accordion: Accordion,
	SimpleCta: SimpleCta,
	PricingSnapshot: PricingSnapshot,
	ProfileEntryPoints: ProfileEntryPoints,
	CoursesGrid: CoursesGrid,
	CollectionsCarousel: CollectionsCarousel,
	ProductWalkthroughAccordion: ProductWalkthroughAccordion,
} satisfies Record<LandingBlockType, React.ComponentType<any>>

export interface LandingPageRendererProps {
	/**
	 * Landing page data to render
	 */
	data: LandingPageData

	/**
	 * Optional className for the container
	 */
	className?: string

	/**
	 * Whether to show error boundaries for individual blocks
	 */
	showErrors?: boolean
}

/**
 * Error boundary for individual blocks
 */
class BlockErrorBoundary extends React.Component<
	{ children: React.ReactNode; blockType: string },
	{ hasError: boolean }
> {
	constructor(props: { children: React.ReactNode; blockType: string }) {
		super(props)
		this.state = { hasError: false }
	}

	static getDerivedStateFromError() {
		return { hasError: true }
	}

	override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		log.error(`Error rendering ${this.props.blockType} block`, {
			error: error.message,
			componentStack: errorInfo.componentStack,
		})
	}

	override render() {
		if (this.state.hasError) {
			return (
				<div className="rounded-lg border border-red-300 bg-red-50 p-6 text-center">
					<p className="text-sm text-red-900">
						Error rendering {this.props.blockType} block
					</p>
				</div>
			)
		}

		return this.props.children
	}
}

/**
 * Render a landing page from landing page data
 */
export function LandingPageRenderer({
	data,
	className,
	showErrors = false,
}: LandingPageRendererProps) {
	// If no content, show empty state
	if (!data.content || data.content.length === 0) {
		return (
			<div className={className}>
				<div className="flex min-h-[400px] items-center justify-center">
					<p className="text-muted-foreground text-center">
						No blocks added yet. Start building your landing page by adding
						blocks from the editor.
					</p>
				</div>
			</div>
		)
	}

	return (
		<div className={className}>
			{data.content.map((block, index) => {
				const blockKey = block._id || `${block.type}-${index}`
				// Get the component for this block type
				const BlockComponent =
					BLOCK_COMPONENTS[block.type as keyof typeof BLOCK_COMPONENTS]

				// If block type not found, show warning
				if (!BlockComponent) {
					log.warn(`Unknown block type: ${block.type}`)
					return (
						<div
							key={blockKey}
							className="border-primary/20 bg-primary/10 rounded-lg border p-6 text-center"
						>
							<p className="text-primary-900 text-sm">
								Unknown block type: {block.type}
							</p>
						</div>
					)
				}

				const blockElement = React.createElement(BlockComponent as any, {
					...block.props,
				})

				if (showErrors) {
					return (
						<BlockErrorBoundary key={blockKey} blockType={block.type}>
							{blockElement}
						</BlockErrorBoundary>
					)
				}

				return <React.Fragment key={blockKey}>{blockElement}</React.Fragment>
			})}
		</div>
	)
}

/**
 * Export block registry for use in other components
 */
export { BLOCK_COMPONENTS }
